class CJProductConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.controls = {
      metal: this.querySelector('[data-cj-control="metal"]'),
      source: this.querySelector('[data-cj-control="source"]'),
      accent: this.querySelector('[data-cj-control="accent"]'),
      size: this.querySelector('[data-cj-control="size"]'),
      antiquing: this.querySelector('[data-cj-control="antiquing"]'),
    };
    this.nativeOptions = Array.from(this.querySelectorAll('[data-cj-native-option]'))
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
    this.status = this.querySelector('[data-cj-status]');
    this.onControlChange = this.onControlChange.bind(this);
    this.onSubmitCapture = this.onSubmitCapture.bind(this);

    if (!this.hasExpectedNativeOptions()) {
      this.showError('This product is missing the approved 11081D option structure. Do not publish it until the import is corrected.');
      this.setPurchaseState(false);
      return;
    }

    this.restoreVisibleControlsFromNative();
    Object.values(this.controls).forEach((control) => control.addEventListener('change', this.onControlChange));
    this.getProductForm()?.addEventListener('submit', this.onSubmitCapture, true);
    this.syncGoldSourceVisibility();
    this.syncLineItemProperties();
    this.setPurchaseState(Boolean(this.controls.size.value));
    if (this.pendingState) {
      const pendingState = this.pendingState;
      this.pendingState = null;
      this.applyState(pendingState);
    }
    this.syncSelectedMedia();
  }

  disconnectedCallback() {
    this.selectionRequest?.abort();
    window.clearTimeout(this.mediaTimer);
    Object.values(this.controls || {}).forEach((control) => control?.removeEventListener('change', this.onControlChange));
    this.getProductForm()?.removeEventListener('submit', this.onSubmitCapture, true);
  }

  hasExpectedNativeOptions() {
    const names = this.nativeOptions.map((select) => select.dataset.optionName);
    return names.length === 3
      && names[0] === 'Metal and Source'
      && names[1] === 'Accent Gem'
      && names[2] === 'Size Tier and Antiquing';
  }

  getDetails() {
    return this.closest('.productView-details');
  }

  syncSelectedMedia(attempt = 0) {
    window.clearTimeout(this.mediaTimer);
    if (!this.isConnected || !this.dataset.variantMediaId) return;

    const section = this.closest(`#ProductSection-${CSS.escape(this.dataset.sectionId)}`);
    const gallery = section?.querySelector('[data-image-gallery]');
    const mediaKey = `${this.dataset.sectionId}-${this.dataset.variantMediaId}`;
    const thumbnail = gallery?.querySelector(
      `.productView-thumbnail:not(.slick-cloned)[data-media-id="${CSS.escape(mediaKey)}"]`
    );
    const main = gallery?.querySelector('[data-image-gallery-main]');
    const thumbnailSlider = thumbnail?.closest('.slick-initialized');

    if (thumbnailSlider && main?.classList.contains('slick-initialized')
      && !main.slick?.animating && !thumbnailSlider.slick?.animating) {
      thumbnail.click();
      return;
    }

    if (attempt < 100) {
      this.mediaTimer = window.setTimeout(() => this.syncSelectedMedia(attempt + 1), 100);
    } else {
      console.warn('[CJ configurator] Assigned variant image could not be selected in the gallery.', this.dataset.variantMediaId);
    }
  }

  getProductForm() {
    return this.getDetails()?.querySelector(`form#product-form-${this.dataset.productId}`);
  }

  getAddButton() {
    return this.getProductForm()?.querySelector('[name="add"]');
  }

  restoreVisibleControlsFromNative() {
    const metalSource = this.nativeOptions[0].value;
    if (metalSource === 'Platinum' || metalSource === 'Palladium') {
      this.controls.metal.value = metalSource;
    } else {
      const separator = metalSource.lastIndexOf(' — ');
      this.controls.metal.value = metalSource.slice(0, separator);
      this.controls.source.value = metalSource.slice(separator + 3);
    }

    this.controls.accent.value = this.nativeOptions[1].value;
    const nativeFinishValue = this.nativeOptions[2].value;
    this.controls.antiquing.value = nativeFinishValue.startsWith('Antiquing')
      || nativeFinishValue.endsWith(' — Antiquing')
      ? 'Antiquing'
      : 'No antiquing';
  }

  applyState(state) {
    if (!this.controls) {
      this.pendingState = state;
      return;
    }
    Object.entries(state).forEach(([key, value]) => {
      if (this.controls[key] && value !== undefined) this.controls[key].value = value;
    });
    this.syncGoldSourceVisibility();
    this.syncLineItemProperties();
    this.setPurchaseState(Boolean(this.controls.size.value));
  }

  readState() {
    return Object.fromEntries(Object.entries(this.controls).map(([key, control]) => [key, control.value]));
  }

  isGold() {
    return this.controls.metal.value.includes('Gold');
  }

  syncGoldSourceVisibility() {
    const field = this.querySelector('[data-cj-source-field]');
    const showSource = this.isGold();
    field.hidden = !showSource;
    this.controls.source.disabled = !showSource;
    if (showSource && !this.controls.source.value) this.controls.source.value = 'Recycled Gold';
  }

  sizeTierValue() {
    if (!this.controls.size.value) {
      return this.nativeOptions[2].value.split(' — ')[0] || 'Size 5–7.75';
    }

    const size = Number(this.controls.size.value);
    if (size <= 7.75) return 'Size 5–7.75';
    if (size <= 9.75) return 'Size 8–9.75';
    return 'Size 10–12';
  }

  sizeSurcharge() {
    if (!this.controls.size.value) return 0;

    const size = Number(this.controls.size.value);
    if (size <= 7.75) return 0;
    if (size <= 9.75) return 290;
    return 345;
  }

  nativeFinishValue() {
    const finish = this.controls.antiquing.value;
    const surcharge = this.sizeSurcharge();
    const customerSafeValue = `${finish} · ${surcharge ? `$${surcharge}` : 'no'} size surcharge`;
    const legacyValue = `${this.sizeTierValue()} — ${finish}`;
    const availableValues = Array.from(this.nativeOptions[2].options).map((option) => option.value);

    if (availableValues.includes(customerSafeValue)) return customerSafeValue;
    return legacyValue;
  }

  targetNativeValues() {
    const metalSource = this.isGold()
      ? `${this.controls.metal.value} — ${this.controls.source.value}`
      : this.controls.metal.value;
    return [
      metalSource,
      this.controls.accent.value,
      this.nativeFinishValue(),
    ];
  }

  optionValueIds(values) {
    return values.map((value, index) => {
      const option = Array.from(this.nativeOptions[index].options).find((candidate) => candidate.value === value);
      return option?.dataset.optionValueId || null;
    });
  }

  syncLineItemProperties() {
    const values = {
      metal: this.controls.metal.value,
      source: this.isGold() ? this.controls.source.value : '',
      accent: this.controls.accent.value,
      size: this.controls.size.value,
      finish: this.controls.antiquing.value,
    };
    this.querySelectorAll('[data-cj-line-property]').forEach((property) => {
      property.value = values[property.dataset.cjLineProperty] || '';
    });
  }

  setPurchaseState(valid) {
    const button = this.getAddButton();
    if (!button) return;
    const variantAvailable = this.dataset.variantAvailable === 'true';
    button.disabled = !valid || !variantAvailable || this.dataset.loading === 'true';
  }

  showError(message) {
    this.status.textContent = message;
    this.status.classList.add('cj-configurator__status--error');
  }

  showStatus(message) {
    this.status.textContent = message;
    this.status.classList.remove('cj-configurator__status--error');
  }

  onSubmitCapture(event) {
    this.syncLineItemProperties();
    if (this.dataset.loading === 'true' || !this.controls.size.value) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError('Select an exact ring size before adding this ring to the cart.');
      this.controls.size.focus();
    }
  }

  async onControlChange() {
    this.selectionRequest?.abort();
    this.dataset.loading = 'false';
    this.removeAttribute('aria-busy');
    this.syncGoldSourceVisibility();
    this.syncLineItemProperties();
    this.setPurchaseState(Boolean(this.controls.size.value));

    const values = this.targetNativeValues();
    const ids = this.optionValueIds(values);
    if (ids.some((id) => !id)) {
      this.showError('That configuration is not present in the approved 11081D variant matrix.');
      this.setPurchaseState(false);
      return;
    }

    const currentValues = this.nativeOptions.map((select) => select.value);
    if (values.every((value, index) => value === currentValues[index])) {
      if (this.controls.size.value) this.showStatus(`Ring size ${this.controls.size.value} selected.`);
      return;
    }

    await this.renderSelection(ids);
  }

  async renderSelection(ids) {
    this.selectionRequest?.abort();
    const request = new AbortController();
    this.selectionRequest = request;
    const state = this.readState();
    this.dataset.loading = 'true';
    this.setPurchaseState(false);
    this.setAttribute('aria-busy', 'true');
    this.showStatus('Updating price and availability…');

    try {
      const requestUrl = new URL(this.dataset.productUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      requestUrl.searchParams.set('option_values', ids.join(','));
      const response = await fetch(requestUrl.toString(), {
        headers: { Accept: 'text/html' },
        signal: request.signal,
      });
      if (!response.ok) throw new Error(`Section request failed with ${response.status}`);

      const responseText = await response.text();
      if (request.signal.aborted || this.selectionRequest !== request || !this.isConnected) return;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selector = `#ProductSection-${CSS.escape(this.dataset.sectionId)} .productView-details`;
      const replacement = html.querySelector(selector);
      const currentDetails = this.getDetails();
      if (!replacement || !currentDetails) throw new Error('Updated product details were not returned');

      currentDetails.replaceWith(replacement);
      const nextConfigurator = replacement.querySelector('cj-product-configurator');
      if (!nextConfigurator) throw new Error('Updated configurator was not returned');
      customElements.upgrade(nextConfigurator);
      nextConfigurator.applyState(state);

      const selectedVariantId = replacement.querySelector(`form#product-form-${CSS.escape(this.dataset.productId)} input[name="id"]`)?.value;
      if (selectedVariantId) {
        const browserUrl = new URL(window.location.href);
        browserUrl.searchParams.delete('option_values');
        browserUrl.searchParams.set('variant', selectedVariantId);
        window.history.replaceState({}, '', browserUrl.toString());
      }
      nextConfigurator.showStatus(state.size ? `Ring size ${state.size} selected.` : 'Select an exact ring size before adding this ring to the cart.');
    } catch (error) {
      if (request.signal.aborted || this.selectionRequest !== request || !this.isConnected) return;
      console.error('[CJ configurator]', error);
      this.dataset.loading = 'false';
      this.removeAttribute('aria-busy');
      this.showError('The price could not be updated. Please try again before adding this ring to the cart.');
      this.setPurchaseState(false);
    }
  }
}

if (!customElements.get('cj-product-configurator')) {
  customElements.define('cj-product-configurator', CJProductConfigurator);
}
